import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

import { Card, CardContent } from '../../ui/card';

import { cn } from '@/lib/utils';

/**
 * StatsCard — a single KPI tile.
 *
 * Generalized from FeedbackDashboard's inline StatsCard so every cockpit
 * dashboard can render a consistent KPI grid (see CockpitHome). A KPI is a
 * headline metric with an optional icon, subtitle, and directional trend.
 *
 * Props:
 *  - title:    metric label (e.g. "Active Members")
 *  - value:    the headline number/string
 *  - subtitle: optional secondary line
 *  - icon:     optional lucide icon component
 *  - trend:    optional signed number; renders an up/down/flat indicator
 *  - color:    optional Tailwind text color for the value
 *  - className: optional passthrough for the outer Card
 */
export const StatsCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  color = 'text-gray-900 dark:text-gray-100',
  className,
}) => (
  <Card className={className}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
          <p className={cn('text-2xl font-bold', color)}>{value}</p>
          {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
        </div>
        {Icon && (
          <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <Icon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </div>
        )}
      </div>
      {trend !== undefined && (
        <div className="flex items-center mt-2 text-sm">
          {trend > 0 ? (
            <TrendingUp className="w-4 h-4 text-green-600 mr-1" />
          ) : trend < 0 ? (
            <TrendingDown className="w-4 h-4 text-red-600 mr-1" />
          ) : (
            <Minus className="w-4 h-4 text-gray-400 mr-1" />
          )}
          <span className={trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-500'}>
            {trend > 0 ? '+' : ''}{trend}
          </span>
        </div>
      )}
    </CardContent>
  </Card>
);

export default StatsCard;
